import {
    DTOCardFormField,
} from 'app/types/form.types'

import {
    DamageStatus, AddressMainCategory,
    ReportingDepartment,
} from 'app/enums/label.enums'
import {
    SizeLink, TypeLink,
} from 'app/shared-components/SkyNetLink/SkyNetLink.enum'
import {
    Damage,
} from '../Damages.types'

const getDamageRepairFields = (editable): DTOCardFormField<keyof Damage> => {
    return {
        title: 'Damage Repair',
        fields: [
            {
                id: 'column1',
                fields: [{
                    name: 'repairingServiceCenter',
                    componentName: 'LocationObjectSelector',
                    filterAddressMainCategory: AddressMainCategory.SERVICECENTER,
                    disabled: !editable,
                    required: true,
                    labelKey: 'REPAIRING_SERVICE_CENTER',
                }],
            },
            {
                id: 'column2',
                fields: [
                    {
                        name: 'repairedOn',
                        componentName: 'DateTimeSelect',
                        required: true,
                        disableFuture: true,
                        labelKey: 'REPAIRED_ON_UTC',
                    },
                    {
                        name: 'repairComment',
                        componentName: 'LongText',
                        disabled: !editable,
                        labelKey: 'REPAIR_COMMENT',
                    },
                ],
            },
            {
                id: 'column3',
                fields: [
                    {
                        name: 'repairedBy',
                        componentName: 'ContactObjectSelector',
                        required: true,
                        labelKey: 'REPAIRED_BY',
                    },
                    {
                        name: 'repairProcessAttachment',
                        componentName: 'FileDownloadLink',
                        disabled: true,
                        labelKey: 'REPAIR_PROCESS',
                    },
                ],
            },
            {
                id: 'column4',
                fields: [{
                    name: 'repairAttachments',
                    componentName: 'AttachmentsObjectSelector',
                    disabled: !editable,
                    labelKey: 'REPAIR_ATTACHMENT',
                }],
            },
        ],
    }
}

const getDamageUpdateFields = ({
    getAllowedValues, editable, damageStatus, linkedPriorDamage, priorDamageLink,
}): DTOCardFormField<keyof Damage>[] => {
    const mainFields = [
        {
            title: 'Summary',
            fields: [
                {
                    id: 'column1',
                    fields: [
                        {
                            name: 'damageNumber',
                            componentName: 'InputSingleline',
                            disabled: true,
                            labelKey: 'DAMAGE_NUMBER',
                        },
                        {
                            name: 'linkedPriorDamage.id',
                            componentName: 'SkyNetLink',
                            size: SizeLink.S,
                            type: TypeLink.EXTERNAL,
                            text: linkedPriorDamage?.damageNumber,
                            link: priorDamageLink,
                            hidden: !priorDamageLink,
                            labelKey: 'LINKED_PRIOR_DAMAGE',
                        },
                    ],
                },
                {
                    id: 'column2',
                    fields: [
                        {
                            name: 'container',
                            componentName: 'ContainerSelector',
                            disabled: true,
                            labelKey: 'CONTAINER_SERIAL_NUMBER',
                        },
                        {
                            name: 'containerModel',
                            componentName: 'InputSingleline',
                            disabled: true,
                            labelKey: 'CONTAINER_MODEL',
                        },
                        {
                            name: 'createdOn',
                            componentName: 'DateTimeSelect',
                            disabled: true,
                            labelKey: 'CREATED_ON_UTC',
                        },
                    ],
                },
                {
                    id: 'column3',
                    fields: [
                        {
                            name: 'reportedOn',
                            componentName: 'DateTimeSelect',
                            disabled: true,
                            labelKey: 'REPORTED_ON_UTC',
                        },
                        {
                            name: 'reportedBy',
                            componentName: 'ContactObjectSelector',
                            disabled: true,
                            labelKey: 'REPORTED_BY',
                        },
                    ],
                },
                {
                    id: 'column4',
                    fields: [
                        {
                            name: 'repairedOn',
                            componentName: 'DateTimeSelect',
                            disabled: true,
                            labelKey: 'REPAIRED_ON_UTC',
                        },
                        {
                            name: 'repairedBy',
                            componentName: 'ContactObjectSelector',
                            disabled: true,
                            labelKey: 'REPAIRED_BY',
                        },
                    ],
                },
            ],
        },
        {
            title: 'Damage Details',
            fields: [
                {
                    id: 'column1',
                    fields: [
                        {
                            name: 'reportingServiceCenter',
                            componentName: 'LocationObjectSelector',
                            filterAddressMainCategory: AddressMainCategory.SERVICECENTER,
                            disabled: !editable,
                            labelKey: 'REPORTING_SERVICE_CENTER',
                        },
                        {
                            name: 'damageType',
                            componentName: 'DamageTypeSelector',
                            required: true,
                            disabled: !editable,
                            labelKey: 'DAMAGE_TYPE',
                        },
                        {
                            name: 'damageStatus',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(DamageStatus),
                            required: true,
                            useDropdown: true,
                            disabled: !editable,
                            labelKey: 'STATUS',
                        },
                    ],
                },
                {
                    id: 'column2',
                    fields: [{
                        name: 'damageComment',
                        componentName: 'LongText',
                        disabled: !editable,
                        labelKey: 'DAMAGE_COMMENT',
                    }],
                },
                {
                    id: 'column3',
                    fields: [
                        {
                            name: 'reportingDepartment',
                            componentName: 'EnumSelector',
                            allowedValues: getAllowedValues(ReportingDepartment),
                            required: true,
                            useDropdown: true,
                            disabled: !editable,
                            labelKey: 'REPORTING_DEPARTMENT',
                        },
                        {
                            name: 'inspectionProcessAttachment',
                            componentName: 'FileDownloadLink',
                            labelKey: 'INSPECTION_PROCESS',
                        },
                    ],
                },
                {
                    id: 'column4',
                    fields: [{
                        name: 'damageAttachments',
                        componentName: 'AttachmentsObjectSelector',
                        disabled: !editable,
                        labelKey: 'DAMAGE_ATTACHMENT',
                    }],
                },
            ],
        },
    ]

    if (damageStatus === DamageStatus.FIXED) {
        return [
            ...mainFields,
            getDamageRepairFields(editable),
        ] as DTOCardFormField<keyof Damage>[]
    }

    return mainFields as DTOCardFormField<keyof Damage>[]
}

export default getDamageUpdateFields
